import React, { useState } from 'react';
import { Scale, Mail, MapPin, Twitter, Linkedin, Facebook } from 'lucide-react';
import SupportTicketModal from './SupportTicketModal';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [email, setEmail] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setSubscriptionStatus('error');
      setTimeout(() => setSubscriptionStatus(''), 3000);
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Simulate API call - replace with actual newsletter API
      const response = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        setSubscriptionStatus('success');
        setEmail('');
      } else {
        setSubscriptionStatus('error');
      }
    } catch (error) {
      // For now, just show success since we don't have a real API
      setSubscriptionStatus('success');
      setEmail('');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubscriptionStatus(''), 3000);
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <img 
                src="/equalizer-logo.png" 
                alt="TheEqualizer.AI Logo" 
                className="h-8 w-auto object-contain"
              />
              <span className="text-sm font-bold">TheEqualizer.AI</span>
            </div>
            <p className="text-gray-400 leading-relaxed">
              AI-powered assistant providing professional guidance, 
              custom templates, and actionable steps for your matters.
            </p>
            <div className="flex space-x-4">
              <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors duration-200">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors duration-200">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors duration-200">
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Quick Links</h3>
            <ul className="space-y-2">
              <li><a href="#home" className="text-gray-400 hover:text-white transition-colors duration-200">Home</a></li>
              <li><a href="#how-it-works" className="text-gray-400 hover:text-white transition-colors duration-200">How it Works</a></li>
              <li><a href="#legal-issues" className="text-gray-400 hover:text-white transition-colors duration-200">Issues</a></li>
              <li><a href="#pricing" className="text-gray-400 hover:text-white transition-colors duration-200">Pricing</a></li>
              <li><a href="#testimonials" className="text-gray-400 hover:text-white transition-colors duration-200">Testimonials</a></li>
              <li><a href="#faq" className="text-gray-400 hover:text-white transition-colors duration-200">FAQ</a></li>
            </ul>
          </div>

          {/* Legal Services */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Services</h3>
            <ul className="space-y-2">
              <li><a href="#" className="text-gray-400 hover:text-white transition-colors duration-200">Landlord-Tenant</a></li>
              <li><a href="#" className="text-gray-400 hover:text-white transition-colors duration-200">Small Claims</a></li>
              <li><a href="#" className="text-gray-400 hover:text-white transition-colors duration-200">Employment Issues</a></li>
              <li><a href="#" className="text-gray-400 hover:text-white transition-colors duration-200">Contract Disputes</a></li>
              <li><a href="#" className="text-gray-400 hover:text-white transition-colors duration-200">Consumer Protection</a></li>
              <li><a href="#" className="text-gray-400 hover:text-white transition-colors duration-200">Traffic Violations</a></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Contact Us</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-blue-400" />
                <span className="text-gray-400">admin@theequalizer.ai</span>
              </div>
              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-blue-400" />
                <span className="text-gray-400">Houston, TX</span>
              </div>
              
              {/* Contact Support Button */}
              <div className="pt-2">
                <button
                  onClick={() => setShowSupportModal(true)}
                  className="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 text-sm"
                >
                  Contact Support
                </button>
              </div>
            </div>
            
            {/* Newsletter Signup */}
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-white mb-2">Stay Updated</h4>
              <form onSubmit={handleNewsletterSubmit} className="space-y-2">
                <div className="flex">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    disabled={isSubmitting}
                    className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-l-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  />
                  <button 
                    type="submit"
                    disabled={isSubmitting || !email}
                    className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 rounded-r-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                  </button>
                </div>
                {subscriptionStatus === 'success' && (
                  <p className="text-green-400 text-xs">✅ Successfully subscribed to our newsletter!</p>
                )}
                {subscriptionStatus === 'error' && (
                  <p className="text-red-400 text-xs">❌ Please enter a valid email address.</p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              © {currentYear} TheEqualizer.AI. All rights reserved.
            </div>
            <div className="flex space-x-6 text-sm">
              <a href="#" className="text-gray-400 hover:text-white transition-colors duration-200">Privacy Policy</a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors duration-200">Terms of Service</a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors duration-200">Cookie Policy</a>
            </div>
          </div>
        </div>
      </div>

      {/* Support Ticket Modal */}
      <SupportTicketModal 
        isOpen={showSupportModal} 
        onClose={() => setShowSupportModal(false)} 
      />
    </footer>
  );
};

export default Footer;